// UC20 — the medicine nomenclator, one row per MEDICINE.
// per docs/shape-uc-20-medicine-nomenclator.md §5
//
// The owner's ruling of 2026-10-13 (Q-1-13, "like the wireframe, all fields") puts the
// register back at position grain, as wireframes/react/src/pages/AdminCoduri.tsx draws
// it and TC-UC20-08-01 reads it: a row is a medicine, and its codes — each with its type
// and its state — are a column of it («Coduri medicament»). That supersedes the
// one-row-per-code register of 2026-09-10, whose reason (the position view had to fill
// the brand, producer, country and expiry from `codes[0]`) is answered here by reading
// each of them the way the code-descriptive-attributes rule says: the first code's own
// value, else the position's (`registerRowAttributes`).
//
// A code still has its own record and its own editor (`/medicines/code/:code`); a code in
// the «Coduri medicament» cell opens it.
import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudLink, MudTable, MudTag } from '@egov-moldova/mud-react';
import {
  EMPTY_CELL,
  EmptyRegisterMessage,
  RowActionsMenu,
  formatDate,
  onRowOpen,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { TableFooter } from '../../components/TableFooter/TableFooter';
import { usePositionActivation } from './usePositionActivation';
import {
  registerRowAttributes,
  type Medicine,
  type MedicineCode,
  type MedicinePage,
  type MedicineSource,
} from './medicines-api';

// mud-table's own column/row types are not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}
type TableRowData = Record<string, string>;

// The wireframe's columns, in its order, after the leading row-actions track
// (AdminCoduri.tsx:356). The codes column is named for what the TC calls it.
export const REGISTER_COLUMNS = [
  ['commercialName', 'medicines.column.commercial_name'],
  ['dci', 'medicines.column.dci'],
  ['pharmaceuticalForm', 'medicines.column.form'],
  ['dose', 'medicines.column.dose'],
  ['volume', 'medicines.column.volume'],
  ['division', 'medicines.column.division'],
  ['manufacturer', 'medicines.column.manufacturer'],
  ['manufacturerCountry', 'medicines.column.country'],
  ['atcCode', 'medicines.column.atc'],
  ['authorizationExpiresOn', 'medicines.column.authorization_expiry'],
  ['codes', 'medicines.column.codes'],
  ['source', 'medicines.column.source'],
  ['status', 'medicines.column.status'],
  ['updatedAtIso', 'medicines.column.last_change'],
] as const satisfies ReadonlyArray<readonly [string, string]>;

// The columns mud-table renders from row data alone. An inactive position reads muted
// across the row (wireframe `tr.inactiv`), and so does the one just added read bold, so
// both slot these cells to carry a class.
const PLAIN_COLUMNS = [
  'dci',
  'pharmaceuticalForm',
  'dose',
  'volume',
  'division',
  'manufacturer',
  'authorizationExpiresOn',
  'source',
] as const;

// Which class of writer owns a position, said in one word under the date of its last
// change (wireframe AdminCoduri.tsx:372, `· sincronizare`).
const LAST_CHANGE_KEY: Record<MedicineSource, string> = {
  NOMENCLATOR_AUTORIZAT: 'medicines.last_change.sync',
  NOMENCLATOR_NEAUTORIZAT: 'medicines.last_change.sync',
  MANUAL_IMPORT: 'medicines.last_change.import',
  MANUAL_EDIT: 'medicines.last_change.manual',
};

function kindSemantic(kind: MedicineCode['kind']): 'success' | 'neutral' | 'accent' {
  if (kind === 'AUTHORIZED') return 'success';
  if (kind === 'BARCODE') return 'accent';
  return 'neutral';
}

/**
 * The code a medicine opens at: the one its own «Coduri medicament» cell names first.
 * catalog-service orders a position's codes `active DESC, code ASC`, so it is the lowest
 * active code, and the same on every screen. A position can have none — the sync does
 * not make one without — and then there is no record to open.
 */
export function openingCode(medicine: Pick<Medicine, 'codes'>): string | null {
  return medicine.codes[0]?.code ?? null;
}

export function MedicinesRegister({
  query,
  page,
  pageSize,
  onPage,
  onPageSize,
  highlightId,
}: {
  query: UseQueryResult<MedicinePage>;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  /** The position just added: marked in the list (TC-UC20-08-01). */
  highlightId?: string | undefined;
}): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const activation = usePositionActivation();

  const rows = query.data?.medicines ?? [];
  const total = query.data?.total ?? 0;

  const columns: TableColumn[] = [
    // 44px: the shared leading row-actions track (docs/ui-shape.md).
    { key: 'actions', label: t('medicines.column.actions'), width: '44px' },
    ...REGISTER_COLUMNS.map(([key, labelKey]) => ({ key, label: t(labelKey) })),
  ];

  const attributes = rows.map(registerRowAttributes);

  // The name the row shows, which is the name its menu, its confirm dialog and its toast
  // must use: a molecule where the register names a brand reads as the wrong row.
  function medicineTitle(m: Medicine, idx: number): string {
    const brand = attributes[idx]?.commercialName || m.dci;
    return [brand, m.pharmaceuticalForm, m.dose].filter(Boolean).join(' ');
  }

  const tableRows: TableRowData[] = rows.map((m, idx) => {
    const a = attributes[idx];
    return {
      id: m.id,
      actions: '',
      commercialName: '',
      dci: m.dci,
      pharmaceuticalForm: m.pharmaceuticalForm,
      dose: m.dose,
      volume: m.volume || EMPTY_CELL,
      division: m.division || EMPTY_CELL,
      manufacturer: a?.manufacturer || EMPTY_CELL,
      manufacturerCountry: '',
      atcCode: '',
      authorizationExpiresOn: a?.authorizationExpiresOn
        ? formatDate(a.authorizationExpiresOn)
        : EMPTY_CELL,
      codes: '',
      source: t(`medicines.source.${m.source}`),
      status: '',
      updatedAtIso: '',
    };
  });

  function cellClass(m: Medicine): string {
    if (m.id === highlightId) return 'simsm-medicines__cell simsm-medicines__cell--highlight';
    return m.active
      ? 'simsm-medicines__cell'
      : 'simsm-medicines__cell simsm-medicines__cell--muted';
  }

  function openPosition(m: Medicine): void {
    const code = openingCode(m);
    if (code) navigate(`/system-admin/medicines/code/${encodeURIComponent(code)}`);
  }

  // Same shape as every register: the table keeps its head, frame and footer when empty
  // and puts the message in one centred cell.
  const register = (
    <>
      <div className="simsm-medicines__table">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="zebra"
          disableSort={true}
          aria-label={t('medicines.title')}
          // The whole row opens the medicine, as the wireframe's `tr.clickable` does;
          // mud-table leaves the kebab's and the codes' own clicks alone.
          hoverable
          onMudRowClick={onRowOpen((index) => {
            const row = rows[index];
            if (row) openPosition(row);
          })}
        >
          <EmptyRegisterMessage>{t('medicines.empty')}</EmptyRegisterMessage>
          <span slot="header-cell-actions" className="visually-hidden">
            {t('medicines.column.actions')}
          </span>
          {rows.map((m, idx) => {
            const firstCode = openingCode(m);
            return (
              <Fragment key={m.id}>
                <span slot={`cell-actions-${idx}`} className="simsm-medicines__cell-actions">
                  <RowActionsMenu
                    label={t('medicines.actions_for', { name: medicineTitle(m, idx) })}
                    actions={[
                      {
                        label: t('medicines.action.open'),
                        onSelect: () => openPosition(m),
                        disabled: firstCode === null,
                        reason: t('medicines.action.no_code'),
                      },
                      {
                        label: t('medicines.action.edit'),
                        onSelect: () =>
                          navigate(
                            `/system-admin/medicines/code/${encodeURIComponent(firstCode ?? '')}/edit`,
                          ),
                        disabled: firstCode === null,
                        reason: t('medicines.action.no_code'),
                      },
                      {
                        label: t('history.action.open'),
                        onSelect: () => navigate(`/system-admin/medicines/${m.id}/history`),
                      },
                      // Position-level, beside the code-level switch on a code's own
                      // page (TC-UC20-11-02 steps 2-3).
                      {
                        label: m.active
                          ? t('medicines.action.deactivate_position')
                          : t('medicines.action.activate_position'),
                        onSelect: () =>
                          void activation.toggle({
                            id: m.id,
                            active: m.active,
                            name: medicineTitle(m, idx),
                          }),
                        disabled: activation.isPending,
                        danger: m.active,
                        separator: true,
                      },
                    ]}
                  />
                </span>
                {/* The one column that wraps (wireframe `td.wrap`); the name is the
                    keyboard-reachable way into the record, as mud-table gives its rows no
                    focusable element of their own. */}
                <span
                  slot={`cell-commercialName-${idx}`}
                  className={`simsm-medicines__cell-name${m.id === highlightId ? ' simsm-medicines__cell--highlight' : ''}`}
                  {...(m.id === highlightId ? { 'aria-current': 'true' as const } : {})}
                >
                  {firstCode ? (
                    <MudLink
                      href={`/system-admin/medicines/code/${encodeURIComponent(firstCode)}`}
                      size="sm"
                      variant="strict"
                      underline="hover"
                      onClick={(event) => {
                        event.preventDefault();
                        openPosition(m);
                      }}
                    >
                      {attributes[idx]?.commercialName || m.dci}
                    </MudLink>
                  ) : (
                    attributes[idx]?.commercialName || m.dci
                  )}
                  {m.id === highlightId ? (
                    <MudTag
                      variant="status"
                      semantic="success"
                      label={t('medicines.highlight.new')}
                    />
                  ) : null}
                </span>
                <span slot={`cell-atcCode-${idx}`} className={cellClass(m)}>
                  <code>{m.atcCode || EMPTY_CELL}</code>
                </span>
                {/* The producer's country when a record states one, otherwise the
                    authorisation holder's — the only country either AMDM feed states. The
                    title names the second case rather than passing it off as the first. */}
                <span
                  slot={`cell-manufacturerCountry-${idx}`}
                  className={cellClass(m)}
                  {...(attributes[idx]?.countryIsHolder
                    ? { title: t('medicines.column.country_holder') }
                    : {})}
                >
                  {attributes[idx]?.country || EMPTY_CELL}
                </span>
                {/* Every code of the medicine, with its type and its state: the column
                    TC-UC20-08-01 asks for. An inactive code is marked, not hidden. */}
                <span slot={`cell-codes-${idx}`} className="simsm-medicines__cell-codes-list">
                  {m.codes.length === 0
                    ? EMPTY_CELL
                    : m.codes.map((code) => (
                        <span key={code.code} className="simsm-medicines__code">
                          <MudLink
                            href={`/system-admin/medicines/code/${encodeURIComponent(code.code)}`}
                            size="sm"
                            variant="strict"
                            underline="hover"
                            onClick={(event) => {
                              event.preventDefault();
                              navigate(
                                `/system-admin/medicines/code/${encodeURIComponent(code.code)}`,
                              );
                            }}
                          >
                            <code>{code.code}</code>
                          </MudLink>
                          <MudTag
                            variant="status"
                            semantic={kindSemantic(code.kind)}
                            label={t(`medicines.kind.${code.kind}`)}
                          />
                          {code.active ? null : (
                            <MudTag
                              variant="status"
                              semantic="neutral"
                              label={t('medicines.status.inactive')}
                            />
                          )}
                        </span>
                      ))}
                </span>
                <span slot={`cell-status-${idx}`}>
                  <MudTag
                    variant="status"
                    semantic={m.active ? 'success' : 'neutral'}
                    label={m.active ? t('medicines.status.active') : t('medicines.status.inactive')}
                  />
                </span>
                {/* The date over the provenance of the position, which is the wireframe's
                    second branch. Its first — the name of whoever made the last manual
                    correction — has nowhere to come from: the trail records the IDNP
                    and SIMSM stores no user accounts. */}
                <span slot={`cell-updatedAtIso-${idx}`} className="simsm-medicines__cell-stamp">
                  <span className={cellClass(m)}>{formatDate(m.updatedAtIso)}</span>
                  <span className="simsm-medicines__meta">{t(LAST_CHANGE_KEY[m.source])}</span>
                </span>
                {m.active && m.id !== highlightId
                  ? null
                  : PLAIN_COLUMNS.map((key) => (
                      <span key={key} slot={`cell-${key}-${idx}`} className={cellClass(m)}>
                        {tableRows[idx]?.[key]}
                      </span>
                    ))}
              </Fragment>
            );
          })}
        </MudTable>
      </div>

      <TableFooter
        total={total}
        page={page}
        pageSize={pageSize}
        onPage={onPage}
        onPageSize={onPageSize}
      />
    </>
  );

  return (
    <QueryState query={query} isEmpty={rows.length === 0} empty={register}>
      {register}
    </QueryState>
  );
}
